import React, {
	type SetStateAction,
	type Dispatch,
	type MouseEvent,
	type PropsWithChildren,
} from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import { Flex, Box } from '@atlaskit/primitives/compiled';
import ShrinkVerticalIcon from '@atlaskit/icon/core/shrink-vertical';

import { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers';

import { ReplyButton } from './ReplyButton';

const styles = cssMap({
	buttonsContainer: {
		justifyContent: 'space-between',
		paddingLeft: token('space.300'),
		paddingTop: token('space.100'),
	},
	editorContainer: {
		flexGrow: 1,
	},
});

const messages = defineMessages({
	hideReplies: {
		id: 'comments-common.comments.replies.footer.hideReplies',
		defaultMessage: '{numOfVisibleReplies, plural, one {Hide reply} other {Hide replies}}',
		description: 'Label for the button to hide comment replies',
	},
});

type RepliesFooterProps = {
	replyView: ViewReplyOptions;
	setReplyView: Dispatch<SetStateAction<ViewReplyOptions>>;
	expandReplyEditor: boolean;
	numOfVisibleReplies: number;
	isParentCommentOpen: boolean;
	isStandaloneComment?: boolean;
	handleReplyButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

export const RepliesFooter = ({
	replyView,
	setReplyView,
	expandReplyEditor,
	numOfVisibleReplies,
	isParentCommentOpen,
	children: replyEditor,
	handleReplyButtonClick,
	isStandaloneComment = false,
}: PropsWithChildren<RepliesFooterProps>) => {
	const { formatMessage } = useIntl();

	const isHideRepliesToggleVisible =
		!expandReplyEditor &&
		!isStandaloneComment &&
		replyView !== ViewReplyOptions.HIDDEN &&
		numOfVisibleReplies > 0;

	return (
		<Flex testId="replies-footer" xcss={styles.buttonsContainer} alignItems="center">
			{expandReplyEditor ? null /* Comment editor's avatar to be added */ : (
				<ReplyButton
					isParentCommentOpen={isParentCommentOpen}
					isStandaloneComment={isStandaloneComment}
					handleReplyButtonClick={handleReplyButtonClick}
				/>
			)}
			{isHideRepliesToggleVisible && (
				<Button
					appearance="subtle"
					iconBefore={ShrinkVerticalIcon}
					onClick={(event: MouseEvent<HTMLButtonElement>) => {
						event.stopPropagation();
						setReplyView(ViewReplyOptions.HIDDEN);
					}}
				>
					{formatMessage(messages.hideReplies, { numOfVisibleReplies })}
				</Button>
			)}
			{expandReplyEditor && (
				<Box testId="reply-editor-container" xcss={styles.editorContainer}>
					{replyEditor}
				</Box>
			)}
		</Flex>
	);
};
